<template>
  <div id="content-schedule" class="p-4 max-w-4xl mx-auto">
    <AppLoading
      v-if="isLoadingConfiguration || isRefetchingConfiguration"
      wrapper-class="h-80"
    />
    <FreeTrialScheduledTask v-else-if="!isSchedulingAllowed" class="min-h-[50vh]" />
    <template v-else>
      <div>
        <p v-if="!isWordpress" class="mb-4 text-sm">
          {{ $t("words.preview") }}
        </p>
        <AppLoading v-if="isLoading" wrapper-class="h-40" />
        <AppError v-else-if="isError" class="h-40" />
        <template v-else-if="selectedContent">
          <!-- <MarkdownEditor
            v-if="isWordpress"
            v-model="payload.data.preferredText"
            :initial-data="selectedData.text"
          /> -->
          <div>
            <ContentListOptionCard
              :post="selectedContent"
              :is-active="true"
              :post-type="contentType"
            />
          </div>
          <PostTypeSelector
            v-if="shouldShowPostTypeSelector && 'postType' in payload.data"
            v-model="(payload.data as ScheduledSocialPostData).postType"
            :platform="integrationId"
            :has-video="!!selectedContent.videos?.length"
            :has-images="!!selectedContent.images?.length"
            class="mt-4"
          />
        </template>
        <p v-else class="text-sm text-surface-500 dark:text-surface-400 p-4">
          {{ t("selectedContentNotFound") }}
        </p>
      </div>
      <TikTokPrivacySelector
        v-if="scheduledContentType === 'tiktok' && 'privacyLevel' in payload.data"
        v-model="(payload.data as ScheduledSocialPostData).privacyLevel"
        class="mt-6"
      />
      <template
        v-if="scheduledContentType === 'youtube' && 'privacyStatus' in payload.data"
      >
        <YouTubePrivacySelector
          v-model="(payload.data as YouTubeScheduledPostData).privacyStatus"
          class="mt-6"
        />
        <YouTubeCategorySelector
          v-model="(payload.data as YouTubeScheduledPostData).categoryId"
        />
        <div class="mb-4">
          <p class="text-sm font-semibold mb-2">{{ $t("madeForKids") }}</p>
          <div class="flex flex-col gap-2">
            <div
              v-for="option in madeForKidsOptions"
              :key="String(option.value)"
              class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer duration-200"
              :class="
                (payload.data as YouTubeScheduledPostData).madeForKids === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-surface-300 dark:border-surface-700 hover:border-primary/50'
              "
              role="button"
              @click="
                (payload.data as YouTubeScheduledPostData).madeForKids = option.value
              "
            >
              <i
                :class="[
                  option.icon,
                  (payload.data as YouTubeScheduledPostData).madeForKids === option.value
                    ? 'text-primary'
                    : 'text-surface-500',
                ]"
                class="text-base"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium leading-tight">{{ option.label }}</p>
                <p class="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                  {{ option.description }}
                </p>
              </div>
              <i
                v-if="
                  (payload.data as YouTubeScheduledPostData).madeForKids === option.value
                "
                class="pi pi-check-circle text-primary"
              />
            </div>
          </div>
        </div>
        <div v-if="firstYoutubeVideo" class="mb-4 flex items-start gap-3">
          <Checkbox
            :model-value="!!(payload.data as YouTubeScheduledPostData).postAsShort"
            input-id="schedule-post-as-short"
            binary
            @update:model-value="
              (payload.data as YouTubeScheduledPostData).postAsShort = $event
            "
          />
          <label for="schedule-post-as-short" class="cursor-pointer">
            <p class="text-sm font-medium">{{ $t("postAsYouTubeShort") }}</p>
            <p class="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
              {{ $t("postAsYouTubeShortHint") }}
            </p>
          </label>
        </div>
      </template>

      <div class="mt-6">
        <p class="font-semibold mb-2">{{ $t("selectAccount") }}</p>
        <p v-if="scheduledContentType === 'linkedin'" class="font-semibold mb-2">
          {{ $t("personal") }}
        </p>
        <IntegrationPicker
          v-if="integrationId"
          v-model="payload.data.integrationIds"
          :platform="integrationId"
          :user-integrations="integrations"
          mode="multiple"
        />
        <Message v-if="!hasValidSelection" :closable="false" severity="warn">{{
          $t("selectAtLeastOneAccountForPost")
        }}</Message>
        <Message v-if="tiktokMediaError" :closable="false" severity="warn" class="mt-2">{{
          tiktokMediaError
        }}</Message>
        <Message
          v-if="youtubeMediaError"
          :closable="false"
          severity="warn"
          class="mt-2"
          >{{ youtubeMediaError }}</Message
        >
      </div>
      <div
        v-if="scheduledContentType === 'linkedin' && hasLinkedInOrganizations"
        class="mb-4"
      >
        <h5 class="text-sm font-semibold mt-3 mb-2">{{ $t("organizations") }}</h5>
        <LinkedInOrganizationPicker
          v-model="payload.data.organizationsIds"
          :user-integrations="integrations as IUserIntegration<'linkedin'>[]"
          platform="linkedin"
          mode="multiple"
          class="mt-2"
        />
      </div>
      <p class="font-semibold mt-6 -mb-2">{{ $t("whenShouldItBePosted") }}</p>
      <TaskScheduler
        v-model:schedule-time="payload.scheduleTime"
        expanded
        without-frequency
        :disabled="isPending"
      />
      <AppButton
        label="Schedule"
        class="w-full mt-6"
        data-testid="schedule-content-submit-button"
        :loading="isPending"
        :disabled="isPending || !!tiktokMediaError || !!youtubeMediaError"
        @click="maybeSaveContent"
      />
    </template>

    <AppButton
      :label="$t('buttons.cancel')"
      variant="secondary"
      text
      class="w-full mt-4"
      data-testid="schedule-content-cancel-button"
      @click="emit('close')"
    />

    <!-- <pre>{{ props }}</pre>
    <pre>{{ payload }}</pre> -->
  </div>
</template>

<script setup lang="ts">
import AppError from "@/components/shared/AppError.vue";
import AppLoading from "@/components/shared/AppLoading.vue";
import type { ContentIntegrationType } from "@/utils/content";
import {
  addLinkedInOrganizationsToPayload,
  getNewYouTubeScheduledPostData,
} from "@/utils/content";
import ContentListOptionCard from "../ContentListOptionCard.vue";
import TaskScheduler from "@/components/scheduler/TaskScheduler.vue";
import { useToast } from "vue-toast-notification";

import { cloneDeep, set } from "lodash-es";
import IntegrationPicker from "@/components/integrations/util/IntegrationPicker.vue";
import FreeTrialScheduledTask from "@/components/taskList/FreeTrialScheduledTask.vue";
import type { IUserIntegration } from "@/types/account";
import LinkedInOrganizationPicker from "@/components/integrations/util/LinkedInOrganizationPicker.vue";
import Message from "@/volt/Message.vue";
import PostTypeSelector from "@/components/singleTask/post/PostTypeSelector.vue";
import TikTokPrivacySelector from "@/components/singleTask/post/TikTokPrivacySelector.vue";
import YouTubePrivacySelector from "@/components/singleTask/post/YouTubePrivacySelector.vue";
import YouTubeCategorySelector from "@/components/singleTask/post/YouTubeCategorySelector.vue";
import Checkbox from "@/volt/Checkbox.vue";
import type {
  ScheduledSocialPostData,
  YouTubeScheduledPostData,
} from "@/types/contentCalendar";
import { useYouTubeShortDetection } from "@/composables/youtubeShortDetection";
import { appendShortsHashtag } from "@/utils/content";

const props = defineProps<{
  scheduledContentId: string;
  scheduledContentType: string; //"facebook" | "twitter" | "instagram" | "linkedin" | "wordpress"
}>();

const emit = defineEmits<{
  close: [];
  done: [];
}>();

const { mutate, isPending } = useNewScheduledContentMutation();
// @ts-ignore
const contentType = getContentTypeByIntegrationId(props.scheduledContentType);
const $toast = useToast(defaultToastOptions);
const { user } = useCurrentUser();
const { t } = withI18nHelpers();
const queryClient = useQueryClient();

const { isLoading, selectedContent, isError } = useFormattedAndSelectedContent(
  props.scheduledContentId,
  contentType || undefined
);

const {
  data: configuration,
  isLoading: isLoadingConfiguration,
  isRefetching: isRefetchingConfiguration,
} = useAccountFeatureConfigurationRequest();

const isSchedulingAllowed = computed(() => {
  return !!configuration.value?.createScheduledTasks;
});

const integrationId = props.scheduledContentType as ContentIntegrationType;
const integrationFeatureKeyMap = {
  facebook: "facebook",
  twitter: "twitter",
  instagram: "instagram",
  linkedin: "linkedin",
  wordpress: "wordpress",
  tiktok: "tiktok",
  youtube: "youtube",
};
const integrationKeys = Object.keys(integrationFeatureKeyMap);

const integrations = computed(() =>
  !integrationId
    ? []
    : findIntegrations(user.value?.integrations || [], integrationId, true)
);

const hasLinkedInOrganizations = computed(() => {
  return findLinkedInOrganizations(integrations.value || []).length > 0;
});

const isWordpress = computed(() => props.scheduledContentType === "wordpress");

const hasValidSelection = computed(() => {
  const hasPersonalAccounts = payload.data.integrationIds?.length > 0;
  const hasOrganizations =
    props.scheduledContentType === "linkedin" &&
    payload.data.organizationsIds?.length > 0;

  return hasPersonalAccounts || hasOrganizations;
});

const tiktokMediaError = computed((): string | null => {
  if (integrationId !== "tiktok") return null;
  const videoCount = selectedContent.value?.videos?.length ?? 0;
  if (videoCount === 0) {
    return t("tiktokVideoRequired");
  }
  // Photo support disabled for now — TikTok only supports video posting
  // const imageCount = selectedContent.value?.images?.length ?? 0;
  // if (videoCount === 0 && imageCount === 0) {
  //   return t("emptyMediaMessage");
  // }
  // if (videoCount === 0 && imageCount < 2) {
  //   return t("invalidMediaMessage");
  // }
  return null;
});

const youtubeMediaError = computed((): string | null => {
  if (integrationId !== "youtube") return null;
  const videoCount = selectedContent.value?.videos?.length ?? 0;
  if (videoCount === 0) return t("videoRequiredToPost");
  return null;
});

const firstYoutubeVideo = computed(() =>
  integrationId === "youtube" ? selectedContent.value?.videos?.[0] : undefined
);
const { isShort: detectedShort } = useYouTubeShortDetection(firstYoutubeVideo);

watch(detectedShort, value => {
  if (integrationId !== "youtube") return;
  const data = payload.data as YouTubeScheduledPostData;
  if (value && data.postAsShort === undefined) data.postAsShort = true;
});

const shouldShowPostTypeSelector = computed(() => {
  return (
    selectedContent.value?.videos?.length &&
    (integrationId === "facebook" || integrationId === "instagram")
  );
});

const madeForKidsOptions: {
  value: boolean;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: false,
    label: "No, it's not made for kids",
    description: "This content is not specifically made for children",
    icon: "pi pi-user",
  },
  {
    value: true,
    label: "Yes, it's made for kids",
    description: "This content is specifically made for children",
    icon: "pi pi-heart",
  },
];

const payload = reactive(getNewContentSchedulePayload());

function maybeSaveContent() {
  if (isPending.value) return;
  if (!payload.taskType) {
    $toast.error(t("unsupportedContentType"));
    return;
  }
  if (!payload.data.contentId) {
    $toast.error(t("requiredParameter", { param: "content" }));
    return;
  }

  if (!hasValidSelection.value) {
    $toast.error(t("selectAtLeastOneAccountForPost"));
    return;
  }

  if (tiktokMediaError.value) {
    $toast.error(tiktokMediaError.value);
    return;
  }

  if (youtubeMediaError.value) {
    $toast.error(youtubeMediaError.value);
    return;
  }

  // Build integration payload
  let integrationData = buildPostIntegrationPayload(
    integrationId,
    payload.data.integrationIds,
    user.value?.integrations || []
  );

  if (
    props.scheduledContentType === "linkedin" &&
    payload.data.organizationsIds.length > 0
  ) {
    // Use the updated addLinkedInOrganizationsToPayload function
    integrationData = addLinkedInOrganizationsToPayload(
      integrationData,
      integrations.value as IUserIntegration<"linkedin">[],
      payload.data.organizationsIds,
      payload.data.integrationIds.length > 0
    );
  }

  const body = cloneDeep(payload);
  // @ts-ignore
  delete body.data.integrationIds;
  // @ts-ignore
  delete body.data.organizationsIds;

  if (integrationId === "youtube") {
    const ytData = body.data as YouTubeScheduledPostData;
    if (ytData.postAsShort) {
      ytData.preferredText = appendShortsHashtag(selectedContent.value?.text || "");
    }
  }

  set(body, "data.integrationData", integrationData);
  set(body, "data.connection", integrationId);

  mutate(body, {
    onSuccess() {
      $toast.success(t("contentScheduled"));
      queryClient.invalidateQueries({
        queryKey: ["task-content"],
      });
      emit("done");
    },
    onError(e) {
      console.error(e);
      $toast.error(t("contentScheduleFailed"));
    },
  });
}

onBeforeMount(() => {
  const scheduledContentType = props.scheduledContentType as Exclude<
    ContentIntegrationType,
    "wordpress"
  >;

  if (!integrationKeys.includes(scheduledContentType) || !contentType) {
    emit("close");
    return;
  }

  if (isWordpress.value) payload.data = getNewWordpressPostData();
  else if (scheduledContentType === "youtube")
    payload.data = getNewYouTubeScheduledPostData();
  else payload.data = getNewScheduledSocialPostData(scheduledContentType);
  payload.data.contentId = props.scheduledContentId;
  payload.taskType = contentType;

  // Initialize postType based on platform (only for social posts)
  if (
    !isWordpress.value &&
    scheduledContentType !== "youtube" &&
    "postType" in payload.data
  ) {
    const data = payload.data as ScheduledSocialPostData;
    if (scheduledContentType === "facebook") {
      data.postType = "message";
    } else if (scheduledContentType === "instagram") {
      data.postType = getInstagramPostType(
        selectedContent.value?.images,
        selectedContent.value?.videos
      );
    } else if (scheduledContentType === "tiktok") {
      data.postType = getTikTokPostType(selectedContent.value?.videos);
      if (!data.privacyLevel) data.privacyLevel = "SELF_ONLY";
    }
  }
});

watchEffect(() => {
  if (!payload?.data || props.scheduledContentType === "youtube") return;
  if ("preferredText" in payload.data) {
    (payload.data as ScheduledSocialPostData).preferredText =
      selectedContent.value?.text || "";
  }
});
</script>
